import * as fs from "node:fs";
import * as path from "node:path";
import type { ReleaseManifest } from "@m8t-stack/platform-release";
import { compareSemver } from "./platform-update.js";
import { runAz } from "./az.js";

/** Normalize a version string to the vX.Y.Z form compareSemver expects. */
function toVTag(v: string): string {
  return v.startsWith("v") ? v : `v${v}`;
}

/**
 * The version of the installer release BAKED into the running job image — read
 * from `<repoRoot>/installer/version.json`, the same file the image was built
 * from (`installer/Dockerfile` copies `installer/` into `/opt/m8t/repo`). Returns
 * `null` when there is no baked repo (a founder/dev run with no repo-root marker)
 * or the file is absent/malformed — callers then fall back to the cli.min check.
 */
export function readBakedReleaseVersion(repoRoot: string | null): string | null {
  if (!repoRoot) return null;
  try {
    const raw = fs.readFileSync(path.join(repoRoot, "installer", "version.json"), "utf8");
    const v = (JSON.parse(raw) as { version?: unknown }).version;
    return typeof v === "string" && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

/**
 * Should the updater job self-update its OWN image before applying the target?
 *
 * Two triggers, either fires:
 *  - PRIMARY (baked-release ≠ target installer): the job applies release content
 *    from the repo BAKED into its image (`/opt/m8t/repo`), NOT a per-commit fetch.
 *    So a job whose baked installer release ≠ the target manifest's installer
 *    version carries the WRONG persona/infra content for that target. Hop to the
 *    target's installer image (whose baked content matches), exit, and let the
 *    next tick re-claim and apply with the correct content. `bakedRelease` is the
 *    running image's `installer/version.json` version (see readBakedReleaseVersion).
 *  - SECONDARY (cli.min): the baked CLI engine is below the manifest's minimum
 *    supported CLI — the pre-existing D7 compat hop.
 *
 * The dev sentinel (`0.0.0-dev`, tsx) never self-updates. When `bakedRelease` is
 * absent (founder/dev path), only the cli.min trigger applies (back-compat).
 */
export function needsEngineUpdate(manifest: ReleaseManifest, runningVersion: string, bakedRelease?: string | null): boolean {
  if (runningVersion === "0.0.0-dev") return false; // dev/tsx never self-updates
  if (bakedRelease != null && bakedRelease.length > 0 && bakedRelease !== manifest.components.installer.version) return true;
  const running = toVTag(runningVersion);
  const min = toVTag(manifest.components.cli.min);
  return compareSemver(running, min) < 0;
}

/** First-step escape hatch: bump the updater job's OWN image to the target installer, then exit;
 *  the next cron tick re-executes with the new engine. Uses the installer component pin. */
export async function updateOwnJobImage(opts: {
  subscriptionId: string;
  resourceGroup: string;
  manifest: ReleaseManifest;
  jobName?: string;
}): Promise<void> {
  const inst = opts.manifest.components.installer;
  const image = `${inst.ref}:${inst.tag}`;
  const job = opts.jobName ?? process.env.UPDATER_JOB_NAME;
  if (!job) throw new Error("UPDATER_JOB_NAME unset — cannot self-update the updater job image");
  await runAz([
    "containerapp",
    "job",
    "update",
    "-n",
    job,
    "-g",
    opts.resourceGroup,
    "--subscription",
    opts.subscriptionId,
    "--image",
    image,
    "--only-show-errors",
  ]);
}
